import { Component } from '@theme/component';

/**
 * Tab list over a set of card rails, with the rail arrows scoped to whichever panel is showing.
 *
 * @extends {Component<{
 *   tabs: HTMLButtonElement[],
 *   panels: HTMLElement[],
 *   rails: HTMLElement[],
 *   previous: HTMLButtonElement,
 *   next: HTMLButtonElement,
 * }>}
 */
class OudhTabs extends Component {
  #index = 0;
  #raf = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#onKeydown);
    this.refs.rails?.forEach((rail) => rail.addEventListener('scroll', this.#onScroll, { passive: true }));
    this.#updateArrows();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener('keydown', this.#onKeydown);
    this.refs.rails?.forEach((rail) => rail.removeEventListener('scroll', this.#onScroll));
  }

  get #activeRail() {
    return this.refs.rails?.[this.#index];
  }

  /**
   * @param {Event} event
   *
   * Horizon delegates `on:click` from `document` and hands the method an event whose `target` is
   * rewritten to the annotated element. `currentTarget` is therefore the document while the
   * listener runs, and null by the time anything reads it — so reading `dataset` off it gave NaN
   * and every tab click silently selected nothing.
   */
  select(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const button = event.target.closest('[data-index]');
    if (!(button instanceof HTMLElement)) return;

    this.#show(Number(button.dataset.index));
  }

  /** @param {number} index */
  #show(index) {
    const { tabs, panels } = this.refs;
    if (!tabs || !panels) return;

    if (index === this.#index) return;

    this.#index = index;
    this.setAttribute('data-switched', '');

    tabs.forEach((tab, i) => tab.setAttribute('aria-selected', String(i === index)));
    // Not `hidden`: oudh.css forces `[hidden]` to display:none !important, and on phones every
    // panel must stay visible as its own slider. The section's CSS hides inactive panels from 640px.
    panels.forEach((panel, i) => {
      panel.toggleAttribute('data-inactive', i !== index);
    });

    this.#updateArrows();
  }

  /** Left/right arrow keys move between tabs, per the WAI-ARIA tabs pattern. */
  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    const { tabs } = this.refs;
    if (!tabs?.length) return;
    if (!(event.target instanceof HTMLElement) || event.target.getAttribute('role') !== 'tab') return;

    let next = this.#index;
    if (event.key === 'ArrowRight') next = (this.#index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (this.#index - 1 + tabs.length) % tabs.length;
    else return;

    event.preventDefault();
    this.#show(next);
    tabs[next]?.focus();
  };

  #onScroll = () => {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      this.#updateArrows();
    });
  };

  /**
   * Shared head arrows follow the active rail (tablet/desktop). On phones every panel shows at once
   * with its own `data-panel-arrow` pair, each tracking that panel's rail.
   */
  #updateArrows() {
    const { previous, next, panels, rails } = this.refs;

    const active = this.#activeRail;
    if (active) {
      previous?.toggleAttribute('disabled', OudhTabs.#atStart(active));
      next?.toggleAttribute('disabled', OudhTabs.#atEnd(active));
    }

    panels?.forEach((panel, i) => {
      const rail = rails?.[i];
      if (!rail) return;
      panel.querySelector('[data-panel-arrow="previous"]')?.toggleAttribute('disabled', OudhTabs.#atStart(rail));
      panel.querySelector('[data-panel-arrow="next"]')?.toggleAttribute('disabled', OudhTabs.#atEnd(rail));
    });
  }

  /** @param {HTMLElement} rail */
  static #atStart(rail) {
    return rail.scrollLeft <= 1;
  }

  /** @param {HTMLElement} rail */
  static #atEnd(rail) {
    return rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 1;
  }

  /**
   * The rail an arrow press should move: its own panel's rail for a per-panel arrow, else the
   * active one.
   * @param {Event} [event]
   */
  #railFor(event) {
    const target = event?.target;
    if (target instanceof HTMLElement && target.closest('[data-panel-arrow]')) {
      const panel = target.closest('.oudh-tabs__panel');
      const index = panel ? (this.refs.panels?.indexOf(/** @type {HTMLElement} */ (panel)) ?? -1) : -1;
      if (index >= 0) return this.refs.rails?.[index];
    }
    return this.#activeRail;
  }

  /** @param {HTMLElement | undefined} rail */
  #step(rail) {
    if (!rail) return 0;

    const firstChild = rail.firstElementChild;
    const cardWidth = firstChild instanceof HTMLElement ? firstChild.offsetWidth : 0;
    return Math.max(cardWidth, rail.clientWidth - cardWidth) || rail.clientWidth;
  }

  /** @param {Event} [event] */
  next(event) {
    const rail = this.#railFor(event);
    rail?.scrollBy({ left: this.#step(rail), behavior: 'smooth' });
  }

  /** @param {Event} [event] */
  previous(event) {
    const rail = this.#railFor(event);
    rail?.scrollBy({ left: -this.#step(rail), behavior: 'smooth' });
  }
}

if (!customElements.get('oudh-tabs')) {
  customElements.define('oudh-tabs', OudhTabs);
}
